import { useCallback, useContext, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import BigNumber from 'bignumber.js'
import { v4 as uuidv4 } from 'uuid'
import { routerAbi } from '../config/abi'
import { defaultChainId, routeAssets, TaxAssets, TransactionType } from '../config/constants'
import { getOpenOceanRouterAddress, getRouterAddress } from '../utils/addressHelpers'
import { formatAmount, fromWei, isInvalidAmount, MAX_UINT256, toWei } from '../utils/formatNumber'
import { multicall } from '../utils/multicall'
import { getAllowance, sendContract, sendV3Contract } from '../utils/api'
import { useRouter } from './useContract'
import useWeb3, { useWeb3Wagmi } from './useWeb3'
import { completeTransaction, openTransaction, updateTransaction } from '../state/transactions/actions'
import { getERC20Contract, getWBNBContract } from '../utils/contractHelpers'
import moment from 'moment'
import useRefresh from './useRefresh'
import { FusionsContext } from '../context/FusionsContext'

// const DisabledDexIds = '0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31,32,33,34,35,36,37,38,39,40,41,42,44,45,46'
const EnabledDexIds = '43,47'
const Connectors = '0xf4c8e32eadec4bfe97e0f595add0f4450a863a11,0x52f24a5e03aee338da5fd9df68d2b6fae1178827,0x90c97f71e18723b0cf0dfa30ee176ab653e89f40'

const useQuoteSwap = (fromAsset, toAsset, fromAmount, multihops = true) => {
  const [bestTrade, setBestTrade] = useState()
  const [priceImpact, setPriceImpact] = useState(new BigNumber(0))
  const [quotePending, setQuotePending] = useState(false)
  const fusions = useContext(FusionsContext)

  useEffect(() => {
    const fetchInfo = async () => {
      const bases = routeAssets[defaultChainId].filter(
        (item) => ![fromAsset.address.toLowerCase(), toAsset.address.toLowerCase()].includes(item.address.toLowerCase()),
      )
      const result = []
      // direct pairs
      result.push({
        routes: [
          {
            from: fromAsset.address,
            to: toAsset.address,
            stable: true,
          },
        ],
      })
      result.push({
        routes: [
          {
            from: fromAsset.address,
            to: toAsset.address,
            stable: false,
          },
        ],
      })
      if (multihops) {
        // 1 hop
        bases.forEach((base) => {
          result.push({
            routes: [
              {
                from: fromAsset.address,
                to: base.address,
                stable: true,
              },
              {
                from: base.address,
                to: toAsset.address,
                stable: true,
              },
            ],
            base: [base],
          })
          result.push({
            routes: [
              {
                from: fromAsset.address,
                to: base.address,
                stable: true,
              },
              {
                from: base.address,
                to: toAsset.address,
                stable: false,
              },
            ],
            base: [base],
          })
          result.push({
            routes: [
              {
                from: fromAsset.address,
                to: base.address,
                stable: false,
              },
              {
                from: base.address,
                to: toAsset.address,
                stable: true,
              },
            ],
            base: [base],
          })
          result.push({
            routes: [
              {
                from: fromAsset.address,
                to: base.address,
                stable: false,
              },
              {
                from: base.address,
                to: toAsset.address,
                stable: false,
              },
            ],
            base: [base],
          })
        })
        // 2 hop
        bases.forEach((base) => {
          const otherbases = bases.filter((ele) => ele.address !== base.address)
          otherbases.forEach((otherbase) => {
            // true true true
            result.push({
              routes: [
                {
                  from: fromAsset.address,
                  to: base.address,
                  stable: true,
                },
                {
                  from: base.address,
                  to: otherbase.address,
                  stable: true,
                },
                {
                  from: otherbase.address,
                  to: toAsset.address,
                  stable: true,
                },
              ],
              base: [base, otherbase],
            })
            // true true false
            result.push({
              routes: [
                {
                  from: fromAsset.address,
                  to: base.address,
                  stable: true,
                },
                {
                  from: base.address,
                  to: otherbase.address,
                  stable: true,
                },
                {
                  from: otherbase.address,
                  to: toAsset.address,
                  stable: false,
                },
              ],
              base: [base, otherbase],
            })
            // true false true
            result.push({
              routes: [
                {
                  from: fromAsset.address,
                  to: base.address,
                  stable: true,
                },
                {
                  from: base.address,
                  to: otherbase.address,
                  stable: false,
                },
                {
                  from: otherbase.address,
                  to: toAsset.address,
                  stable: true,
                },
              ],
              base: [base, otherbase],
            })
            // true false false
            result.push({
              routes: [
                {
                  from: fromAsset.address,
                  to: base.address,
                  stable: true,
                },
                {
                  from: base.address,
                  to: otherbase.address,
                  stable: false,
                },
                {
                  from: otherbase.address,
                  to: toAsset.address,
                  stable: false,
                },
              ],
              base: [base, otherbase],
            })
            // false true true
            result.push({
              routes: [
                {
                  from: fromAsset.address,
                  to: base.address,
                  stable: false,
                },
                {
                  from: base.address,
                  to: otherbase.address,
                  stable: true,
                },
                {
                  from: otherbase.address,
                  to: toAsset.address,
                  stable: true,
                },
              ],
              base: [base, otherbase],
            })
            // false true false
            result.push({
              routes: [
                {
                  from: fromAsset.address,
                  to: base.address,
                  stable: false,
                },
                {
                  from: base.address,
                  to: otherbase.address,
                  stable: true,
                },
                {
                  from: otherbase.address,
                  to: toAsset.address,
                  stable: false,
                },
              ],
              base: [base, otherbase],
            })
            // false false true
            result.push({
              routes: [
                {
                  from: fromAsset.address,
                  to: base.address,
                  stable: false,
                },
                {
                  from: base.address,
                  to: otherbase.address,
                  stable: false,
                },
                {
                  from: otherbase.address,
                  to: toAsset.address,
                  stable: true,
                },
              ],
              base: [base, otherbase],
            })
            // false false false
            result.push({
              routes: [
                {
                  from: fromAsset.address,
                  to: base.address,
                  stable: false,
                },
                {
                  from: base.address,
                  to: otherbase.address,
                  stable: false,
                },
                {
                  from: otherbase.address,
                  to: toAsset.address,
                  stable: false,
                },
              ],
              base: [base, otherbase],
            })
          })
        })
      }
      const final = result.filter((item) => {
        let isExist = true
        for (let i = 0; i < item.routes.length; i++) {
          const route = item.routes[i]
          const found = fusions
            .filter((pair) => !pair.isGamma)
            .find((pair) =>
              route.stable
                ? pair.type === 'Stable'
                : pair.type === 'Volatile' &&
                  [pair.token0.address.toLowerCase(), pair.token1.address.toLowerCase()].includes(route.from.toLowerCase()) &&
                  [pair.token0.address.toLowerCase(), pair.token1.address.toLowerCase()].includes(route.to.toLowerCase()),
            )
          if (!found) {
            isExist = false
            break
          }
        }
        return isExist
      })
      if (final.length === 0) {
        setBestTrade(null)
        return
      }
      setQuotePending(true)
      const sendFromAmount = toWei(fromAmount, fromAsset.decimals).toFixed(0)
      const calls = final.map((item) => {
        return {
          address: getRouterAddress(),
          name: 'getAmountsOut',
          params: [sendFromAmount, item.routes],
        }
      })
      try {
        const receiveAmounts = await multicall(routerAbi, calls)

        for (let i = 0; i < receiveAmounts.length; i++) {
          final[i].receiveAmounts = receiveAmounts[i].amounts
          final[i].finalValue = fromWei(Number(receiveAmounts[i].amounts[receiveAmounts[i].amounts.length - 1]), toAsset.decimals)
        }
        const bestAmountOut = final.reduce((best, current) => {
          if (!best) {
            return current
          }
          return best.finalValue.gt(current.finalValue) ? best : current
        }, 0)

        if (bestAmountOut.finalValue.isZero()) {
          setQuotePending(false)
          setBestTrade(null)
          return
        }

        let totalRatio = new BigNumber(1)

        for (let i = 0; i < bestAmountOut.routes.length; i++) {
          const route = bestAmountOut.routes[i]
          if (!route.stable) {
            const found = fusions.find(
              (pair) =>
                pair.stable === route.stable &&
                !pair.isGamma &&
                [pair.token0.address.toLowerCase(), pair.token1.address.toLowerCase()].includes(route.from.toLowerCase()) &&
                [pair.token0.address.toLowerCase(), pair.token1.address.toLowerCase()].includes(route.to.toLowerCase()),
            )
            let reserveIn
            let reserveOut
            if (found.token0.address.toLowerCase() === route.from.toLowerCase()) {
              reserveIn = toWei(found.token0.reserve, found.token0.decimals)
              reserveOut = toWei(found.token1.reserve, found.token1.decimals)
            } else {
              reserveIn = toWei(found.token1.reserve, found.token1.decimals)
              reserveOut = toWei(found.token0.reserve, found.token0.decimals)
            }
            let amountIn = 0
            let amountOut = 0
            if (i == 0) {
              amountIn = sendFromAmount
              amountOut = Number(bestAmountOut.receiveAmounts[1])
            } else {
              amountIn = Number(bestAmountOut.receiveAmounts[i])
              amountOut = Number(bestAmountOut.receiveAmounts[i + 1])
            }

            const amIn = new BigNumber(amountIn).div(reserveIn)
            const amOut = new BigNumber(amountOut).div(reserveOut)
            const ratio = new BigNumber(amOut).div(amIn)

            totalRatio = totalRatio.times(ratio)
          }
        }

        setBestTrade(bestAmountOut)
        setPriceImpact(new BigNumber(1).minus(totalRatio).times(100))
        setQuotePending(false)
      } catch (error) {
        console.log('error :>> ', error)
        setBestTrade(null)
        setQuotePending(false)
      }
    }
    if (fromAsset && toAsset && !isInvalidAmount(fromAmount) && fromAsset.address.toLowerCase() !== toAsset.address.toLowerCase()) {
      fetchInfo()
    } else {
      setBestTrade(null)
    }
  }, [fromAsset, toAsset, fromAmount, fusions, multihops])

  return { bestTrade, priceImpact, quotePending }
}

const useProceedSwap = () => {
  const [swapPending, setSwapPending] = useState(false)
  const { account } = useWeb3Wagmi()
  const dispatch = useDispatch()
  const routerAddress = getRouterAddress()
  const routerContract = useRouter()
  const web3 = useWeb3()

  const handleSwap = useCallback(
    async (fromAsset, toAsset, fromAmount, bestTrade, slippage, deadline) => {
      const key = uuidv4()
      const approveuuid = uuidv4()
      const swapuuid = uuidv4()
      setSwapPending(true)
      dispatch(
        openTransaction({
          key,
          title: `Swap ${fromAsset.symbol} for ${toAsset.symbol}`,
          transactions: {
            [approveuuid]: {
              desc: `Approve ${fromAsset.symbol}`,
              status: TransactionType.WAITING,
              hash: null,
            },
            [swapuuid]: {
              desc: `Swap ${formatAmount(fromAmount)} ${fromAsset.symbol} for ${toAsset.symbol}`,
              status: TransactionType.START,
              hash: null,
            },
          },
        }),
      )

      let isApproved = true
      if (fromAsset.address !== 'ETH') {
        const tokenContract = getERC20Contract(web3, fromAsset.address)
        const allowance = await getAllowance(tokenContract, routerAddress, account)
        if (fromWei(allowance, fromAsset.decimals).lt(fromAmount)) {
          isApproved = false
          try {
            await sendContract(dispatch, key, approveuuid, tokenContract, 'approve', [routerAddress, MAX_UINT256], account)
          } catch (err) {
            console.log('approve 0 error :>> ', err)
            setSwapPending(false)
            return
          }
        }
      }
      if (isApproved) {
        dispatch(
          updateTransaction({
            key,
            uuid: approveuuid,
            status: TransactionType.SUCCESS,
          }),
        )
      }

      const sendSlippage = new BigNumber(100).minus(slippage).div(100)
      const sendFromAmount = toWei(fromAmount, fromAsset.decimals).toFixed(0)
      const sendMinAmountOut = toWei(bestTrade.finalValue.times(sendSlippage), toAsset.decimals).toFixed(0)
      const deadlineVal =
        '' +
        moment()
          .add(Number(deadline) * 60, 'seconds')
          .unix()

      let func =
        TaxAssets.includes(fromAsset.address.toLowerCase()) || TaxAssets.includes(toAsset.address.toLowerCase())
          ? 'swapExactTokensForTokensSupportingFeeOnTransferTokens'
          : 'swapExactTokensForTokens'
      let params = [sendFromAmount, sendMinAmountOut, bestTrade.routes, account, deadlineVal]
      let sendValue = '0'

      if (fromAsset.address === 'ETH') {
        if (TaxAssets.includes(toAsset.address.toLowerCase())) {
          func = 'swapExactETHForTokensSupportingFeeOnTransferTokens'
        } else {
          func = 'swapExactETHForTokens'
        }
        params = [sendMinAmountOut, bestTrade.routes, account, deadlineVal]
        sendValue = sendFromAmount
      }
      if (toAsset.address === 'ETH') {
        if (TaxAssets.includes(fromAsset.address.toLowerCase())) {
          func = 'swapExactTokensForETHSupportingFeeOnTransferTokens'
        } else {
          func = 'swapExactTokensForETH'
        }
      }
      try {
        console.log(func, params, account, sendValue)
        await sendContract(dispatch, key, swapuuid, routerContract, func, params, account, sendValue)
      } catch (err) {
        console.log('swap error :>> ', err)
        setSwapPending(false)
        return
      }

      dispatch(
        completeTransaction({
          key,
          final: 'Swap Successful',
        }),
      )
      setSwapPending(false)
    },
    [account, web3, routerContract],
  )

  const handleWrap = useCallback(
    async (amount) => {
      const key = uuidv4()
      const wrapuuid = uuidv4()
      setSwapPending(true)
      dispatch(
        openTransaction({
          key,
          title: `Wrap ETH for WETH`,
          transactions: {
            [wrapuuid]: {
              desc: `Wrap ${formatAmount(amount)} ETH for WETH`,
              status: TransactionType.START,
              hash: null,
            },
          },
        }),
      )

      const wbnbContract = getWBNBContract(web3)
      try {
        await sendContract(dispatch, key, wrapuuid, wbnbContract, 'deposit', [], account, toWei(amount).toFixed(0))
      } catch (err) {
        console.log('wrap error :>> ', err)
        setSwapPending(false)
        return
      }

      dispatch(
        completeTransaction({
          key,
          final: 'Wrap Successful',
        }),
      )
      setSwapPending(false)
    },
    [account, web3, routerContract],
  )

  const handleUnwrap = useCallback(
    async (amount) => {
      const key = uuidv4()
      const wrapuuid = uuidv4()
      setSwapPending(true)
      dispatch(
        openTransaction({
          key,
          title: `Unwrap WETH for ETH`,
          transactions: {
            [wrapuuid]: {
              desc: `Unwrap ${formatAmount(amount)} WETH for ETH`,
              status: TransactionType.START,
              hash: null,
            },
          },
        }),
      )

      const wbnbContract = getWBNBContract(web3)
      try {
        await sendContract(dispatch, key, wrapuuid, wbnbContract, 'withdraw', [toWei(amount).toFixed(0)], account)
      } catch (err) {
        console.log('unwrap error :>> ', err)
        setSwapPending(false)
        return
      }

      dispatch(
        completeTransaction({
          key,
          final: 'Unwrap Successful',
        }),
      )
      setSwapPending(false)
    },
    [account, web3, routerContract],
  )

  return { onSwap: handleSwap, onWrap: handleWrap, onUnwrap: handleUnwrap, swapPending }
}

const useBestQuoteSwap = (fromAsset, toAsset, fromAmount, slippage) => {
  const [bestTrade, setBestTrade] = useState()
  const [quotePending, setQuotePending] = useState(false)
  const { fastestRefresh } = useRefresh()

  useEffect(() => {
    const fetchQuoteInfo = async () => {
      setQuotePending(true)
      try {
        const inTokenAddress = fromAsset.address === 'BNB' ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE' : fromAsset.address
        const outTokenAddress = toAsset.address === 'BNB' ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE' : toAsset.address
        const response = await fetch(
          `https://open-api.openocean.finance/v3/bsc/quote?chain=bsc&inTokenAddress=${inTokenAddress}&outTokenAddress=${outTokenAddress}&amount=${Number(
            fromAmount,
          )}&gasPrice=5&slippage=${slippage}&enabledDexIds=${EnabledDexIds}&referrer=0x7aefe9316fe9eff671da6edf4eeafaa93380f197&connectors=${Connectors}`,
          {
            method: 'GET',
          },
        )
        const res = await response.json()
        setBestTrade(res.data)
      } catch (error) {
        console.log('best swap error :>> ', error)
        setBestTrade(null)
      }
      setQuotePending(false)
    }
    if (fromAsset && toAsset && !isInvalidAmount(fromAmount) && fromAsset.address.toLowerCase() !== toAsset.address.toLowerCase()) {
      fetchQuoteInfo()
    } else {
      setBestTrade(null)
    }
  }, [fromAsset, toAsset, fromAmount, fastestRefresh])

  return { bestTrade, quotePending }
}

const useBestSwap = () => {
  const [swapPending, setSwapPending] = useState(false)
  const { account } = useWeb3Wagmi()
  const dispatch = useDispatch()
  const web3 = useWeb3()

  const handleBestSwap = useCallback(
    async (fromAsset, toAsset, fromAmount, toAmount, slippage) => {
      const key = uuidv4()
      const approveuuid = uuidv4()
      const swapuuid = uuidv4()
      const routerAddress = getOpenOceanRouterAddress()
      setSwapPending(true)
      dispatch(
        openTransaction({
          key,
          title: `Swap ${fromAsset.symbol} for ${toAsset.symbol}`,
          transactions: {
            [approveuuid]: {
              desc: `Approve ${fromAsset.symbol}`,
              status: TransactionType.WAITING,
              hash: null,
            },
            [swapuuid]: {
              desc: `Swap ${formatAmount(fromAmount)} ${fromAsset.symbol} for ${formatAmount(toAmount)} ${toAsset.symbol}`,
              status: TransactionType.START,
              hash: null,
            },
          },
        }),
      )

      let isApproved = true
      if (fromAsset.address !== 'BNB') {
        const tokenContract = getERC20Contract(web3, fromAsset.address)
        const allowance = await getAllowance(tokenContract, routerAddress, account)
        if (fromWei(allowance, fromAsset.decimals).lt(fromAmount)) {
          isApproved = false
          try {
            await sendContract(dispatch, key, approveuuid, tokenContract, 'approve', [routerAddress, MAX_UINT256], account)
          } catch (err) {
            console.log('approve 0 error :>> ', err)
            setSwapPending(false)
            return
          }
        }
      }
      if (isApproved) {
        dispatch(
          updateTransaction({
            key,
            uuid: approveuuid,
            status: TransactionType.SUCCESS,
          }),
        )
      }
      const inTokenAddress = fromAsset.address === 'BNB' ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE' : fromAsset.address
      const outTokenAddress = toAsset.address === 'BNB' ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE' : toAsset.address
      const response = await fetch(
        `https://open-api.openocean.finance/v3/bsc/swap_quote?inTokenAddress=${inTokenAddress}&outTokenAddress=${outTokenAddress}&amount=${new BigNumber(
          fromAmount,
        )
          .dp(fromAsset.decimals)
          .toString(
            10,
          )}&gasPrice=5&slippage=${slippage}&account=${account}&enabledDexIds=${EnabledDexIds}&referrer=0x7aefe9316fe9eff671da6edf4eeafaa93380f197&connectors=${Connectors}`,
        {
          method: 'GET',
        },
      )
      const res = await response.json()
      const { data, value } = res.data
      try {
        await sendV3Contract(dispatch, key, swapuuid, web3, account, routerAddress, data, value)
      } catch (err) {
        console.log('best swap error :>> ', err)
        setSwapPending(false)
        return
      }

      dispatch(
        completeTransaction({
          key,
          final: 'Swap Successful',
        }),
      )
      setSwapPending(false)
    },
    [account, web3],
  )

  return { onBestSwap: handleBestSwap, swapPending }
}

export { useQuoteSwap, useProceedSwap, useBestQuoteSwap, useBestSwap }
